import { FC, Fragment, useContext, useMemo, useState } from 'react';
import { createContext } from 'react';
import { BKJSelect, BKJSelectSearch } from '@bookjane2/bookjane-design-library';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { useTranslation } from 'hooks/useTranslation';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useResponsive } from 'providers/ResponsiveProvider/Responsive';
import { IGroupedCustomer } from 'components/GroupedCustomersDropdown/GroupedCustomersDropdown.types';
import {
  CustomerGroupHeader,
  CustomerOptionListItem,
  NoResults,
} from 'components/GroupedCustomersDropdown/GroupedCustomersDropdown.styled';

const FALLBACK_GROUP = '';

export type CustomersGroupedOptionsType = Record<string, IGroupedCustomer[]>;

type CustomersDataContextType = {
  data: { options: CustomersGroupedOptionsType };
  values: { byName: string };
  onChange: (values: { byName: string }) => void;
  allCustomers: IGroupedCustomer[];
};

const CustomersDataContext = createContext<CustomersDataContextType | null>(null);

function groupCustomersByCustomerName(customers: IGroupedCustomer[]): CustomersGroupedOptionsType {
  const grouped: CustomersGroupedOptionsType = {};
  for (const customer of customers) {
    const key = customer.customerName ?? FALLBACK_GROUP;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(customer);
  }
  return grouped;
}

function filterGroupedOptions(
  options: CustomersGroupedOptionsType,
  searchLower: string,
): CustomersGroupedOptionsType {
  if (!searchLower) return options;
  const filtered: CustomersGroupedOptionsType = {};
  for (const [groupName, list] of Object.entries(options)) {
    const matching = list.filter(
      (c) =>
        c.name.toLowerCase().includes(searchLower) ||
        (c.address && c.address.toLowerCase().includes(searchLower)) ||
        (c.customerName && c.customerName.toLowerCase().includes(searchLower)),
    );
    if (matching.length) filtered[groupName] = matching;
  }
  return filtered;
}

/**
 * Single (Schedule): number | undefined.
 * Multi (Create Shifts): IGroupedCustomer[] so we can derive id/name without lookups.
 */
export type GroupedCustomersDropdownValue = number | undefined | IGroupedCustomer[];

function isCustomerArray(value: GroupedCustomersDropdownValue): value is IGroupedCustomer[] {
  return Array.isArray(value);
}

export type GroupedCustomersDropdownVariant = 'single' | 'multi';

export interface IGroupedCustomersDropdownProps {
  name: string;
  variant: GroupedCustomersDropdownVariant;
  value: GroupedCustomersDropdownValue;
  onChange: (event: UseFormBehaviorsEventType | UseFormBehaviorsEventType[]) => void;
  error?: string | string[] | boolean;
  disabled?: boolean;
  customersData?: IGroupedCustomer[];
  isLoading?: boolean;
  width?: string;
  placeholder?: string;
  /** Dropdown panel width; on mobile defaults to viewport-based width when not set */
  dropdownWidth?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const RenderCustomersGroup = ({
  name,
  selected,
  onChange: _onChange,
}: {
  name: string;
  selected: string[];
  onChange: (e: ChangeEventType) => void;
}) => {
  const { t } = useTranslation();
  const ctx = useContext(CustomersDataContext);
  if (!ctx) return null;
  const { data, values, onChange: onSearchChange } = ctx;
  const options = data?.options ?? {};
  const groupNames = Object.keys(options);

  const onSelectCustomer = (customer: IGroupedCustomer) => {
    _onChange({
      target: {
        name,
        value: customer.id,
        label: customer.name,
      },
    });
  };

  return (
    <Fragment>
      <BKJSelectSearch
        onChange={(e: ChangeEventType) => {
          const v = e?.target?.value;
          onSearchChange({ byName: typeof v === 'string' ? v : '' });
        }}
        name="byName"
        value={values.byName}
        placeholder={t('common:SEARCH')}
        debounceMs={200}
      />
      {groupNames.length === 0 ? (
        <NoResults>{t('common:NO_RESULTS')}</NoResults>
      ) : (
        groupNames.map((groupName) => (
          <Fragment key={groupName}>
            <CustomerGroupHeader>{groupName || 'Other'}</CustomerGroupHeader>
            {options[groupName].map((customer) => (
              <CustomerOptionListItem
                key={customer.id}
                id={String(customer.id)}
                value={customer.id}
                label={customer.name?.trim() || customer.customerName?.trim() || groupName || '—'}
                subLabel={customer.address?.trim() ?? ''}
                name={name}
                onChange={() => onSelectCustomer(customer)}
                selected={selected}
              />
            ))}
          </Fragment>
        ))
      )}
    </Fragment>
  );
};

const CustomersDataProvider: FC<{
  children: React.ReactNode;
  customersData?: IGroupedCustomer[];
}> = ({ children, customersData = [] }) => {
  const [searchValue, setSearchValue] = useState('');

  const groupedOptions = useMemo(
    () => groupCustomersByCustomerName(customersData),
    [customersData],
  );

  const filteredOptions = useMemo(() => {
    const searchLower =
      typeof searchValue === 'string' && searchValue.trim() ? searchValue.trim().toLowerCase() : '';
    return filterGroupedOptions(groupedOptions, searchLower);
  }, [groupedOptions, searchValue]);

  const allCustomers = useMemo(() => customersData, [customersData]);

  const contextValue: CustomersDataContextType = {
    data: { options: filteredOptions },
    values: { byName: searchValue },
    onChange: (values: { byName: string }) => setSearchValue(values.byName ?? ''),
    allCustomers,
  };

  return (
    <CustomersDataContext.Provider value={contextValue}>{children}</CustomersDataContext.Provider>
  );
};

const GroupedCustomersDropdownComponent: FC<IGroupedCustomersDropdownProps> = ({
  name,
  variant,
  value,
  onChange,
  error,
  disabled = false,
  isLoading = false,
  width,
  placeholder,
  dropdownWidth: dropdownWidthProp,
  customersData = [],
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  const ctx = useContext(CustomersDataContext);
  const { isMobile } = useResponsive();
  const isSingle = variant === 'single';

  const dropdownWidth =
    dropdownWidthProp ?? (isMobile ? 'min(500px, calc(100vw - 32px))' : '500px');

  const selectedId = isCustomerArray(value) ? value[0]?.id : value;
  const selectedCustomer = isCustomerArray(value)
    ? value[0]
    : ctx?.allCustomers.find((c) => c.id === selectedId);
  const selectedIds = selectedId != null ? [String(selectedId)] : [];
  const displayLabel = selectedCustomer?.name;

  const handleSelectChange = (e: ChangeEventType) => {
    const raw = Array.isArray(e?.target?.value) ? e.target.value[0] : e?.target?.value;
    const isEmpty = raw == null || raw === '';

    if (isEmpty) {
      if (isSingle) {
        onChange({ target: { name, value: undefined } });
      } else {
        onChange({ target: { name, value: [] } });
        onChange({ target: { name: 'selectedCustomer', value: undefined } });
      }
      return;
    }

    const customer = ctx?.allCustomers.find((c) => c.id === Number(raw));
    if (customer) {
      if (isSingle) {
        onChange({ target: { name, value: customer.id } });
      } else {
        onChange({
          target: {
            name,
            value: [customer] as unknown as UseFormBehaviorsEventType['target']['value'],
          },
        });
        onChange({
          target: {
            name: 'selectedCustomer',
            value: customer as unknown as UseFormBehaviorsEventType['target']['value'],
          },
        });
      }
    }
  };

  const hasError =
    error != null &&
    (Array.isArray(error) ? error.length > 0 : typeof error === 'string' && error.length > 0);
  const resolvedError = hasError ? error : undefined;

  const placeholderColor = !displayLabel ? 'TextDisabled' : undefined;

  return (
    <BKJSelect
      name={name}
      type="Single"
      options={[]}
      placeholder={placeholder ?? (isLoading ? 'Loading customers...' : 'Select Customer')}
      label={displayLabel}
      variant="GreyOutlined"
      width={width ?? '100%'}
      error={resolvedError}
      disabled={disabled}
      dropdownWidth={dropdownWidth}
      dropdownVariant="Square"
      dropdownProps={{ dropdownListItemVariant: 'List', isSearchable: true }}
      dropdownPlacement="bottom-start"
      value={selectedIds}
      onChange={handleSelectChange}
      renderOptions={RenderCustomersGroup}
      placeholderColor={placeholderColor}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
    />
  );
};

export const GroupedCustomersDropdown: FC<IGroupedCustomersDropdownProps> = ({
  customersData,
  variant,
  ...props
}) => {
  return (
    <CustomersDataProvider customersData={customersData}>
      <GroupedCustomersDropdownComponent
        {...props}
        variant={variant}
        customersData={customersData}
      />
    </CustomersDataProvider>
  );
};
